import { useNavigate, useParams } from 'react-router-dom';
import { useStorefrontTenantSlug } from '@/contexts/StorefrontTenantContext';

/** When URL params are missing, infer storefront base from pathname (e.g. /store/slug/checkout → /store/slug). */
function inferStoreBaseFromPathname(pathname: string, tenantSlug: string | null): string {
    if (tenantSlug) return '';
    if (pathname.startsWith('/preview/editor/')) {
        const id = pathname.split('/')[3];
        return id ? `/preview/editor/${id}` : '';
    }
    if (pathname.startsWith('/preview/')) {
        const id = pathname.split('/')[2];
        return id && id !== 'editor' ? `/preview/${id}` : '';
    }
    if (pathname.startsWith('/demo-store/')) {
        const id = pathname.split('/')[2];
        return id ? `/demo-store/${id}` : '';
    }
    if (pathname.startsWith('/store/')) {
        const slug = pathname.split('/')[2];
        return slug ? `/store/${slug}` : '';
    }
    if (pathname.startsWith('/s/')) {
        const id = pathname.split('/')[2];
        return id ? `/s/${id}` : '';
    }
    return '';
}

export function useSmartNavigate() {
    const navigate = useNavigate();
    const tenantSlug = useStorefrontTenantSlug();
    const { templateId, storeId, storeSlug } = useParams<{ templateId?: string; storeId?: string; storeSlug?: string }>();

    const getSmartPath = (path: string) => {
        if (tenantSlug) {
            if (path.startsWith('http') || path.startsWith('//')) return path;
            if (path.startsWith('#')) return path;
            if (path === '/' || path === '') return '/';
            return path.startsWith('/') ? path : `/${path}`;
        }

        // Strict namespace lock based on CURRENT URL
        let baseUrl = '';
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        if (pathname.startsWith('/preview/editor/')) {
            const segments = pathname.split('/');
            const id = storeId ?? segments[3];
            baseUrl = `/preview/editor/${id}`;
        } else if (pathname.startsWith('/preview/')) {
            const id = storeId || pathname.split('/')[2];
            baseUrl = `/preview/${id}`;
        } else if (pathname.startsWith('/demo-store/')) {
            const id = templateId || pathname.split('/')[2];
            baseUrl = `/demo-store/${id}`;
        } else if (pathname.startsWith('/store/')) {
            const slug = storeSlug || pathname.split('/')[2];
            baseUrl = `/store/${slug}`;
        } else if (pathname.startsWith('/s/')) {
            const id = storeId || pathname.split('/')[2];
            baseUrl = `/s/${id}`;
        }

        if (!baseUrl) {
            baseUrl = inferStoreBaseFromPathname(pathname, tenantSlug);
        }

        if (!baseUrl) return path;
        if (path.startsWith('http')) return path;
        if (path.startsWith('#')) return path;

        if (path === '/' || path === '') {
            return baseUrl;
        }

        const safePath = path.startsWith('/') ? path : `/${path}`;

        // `useStorePath` + `hrefForSectionButton` already emit `/store/{slug}/…`.
        // `SmartLink` passes that full href into `getSmartPath` — do not prefix twice.
        if (baseUrl && (safePath === baseUrl || safePath.startsWith(`${baseUrl}/`))) {
            return safePath;
        }

        return `${baseUrl}${safePath}`;
    };

    const smartNavigate = (path: string) => {
        const apex = import.meta.env.VITE_PUBLIC_APP_ORIGIN?.replace(/\/$/, '');
        if (
            tenantSlug &&
            apex &&
            (path === '/login' ||
                path === '/signup' ||
                path.startsWith('/auth') ||
                path.startsWith('/verify-email') ||
                path.startsWith('/forgot-password') ||
                path.startsWith('/reset-password'))
        ) {
            window.location.href = `${apex}${path.startsWith('/') ? path : `/${path}`}`;
            return;
        }

        if (path.startsWith('http')) {
            window.location.href = path;
            return;
        }
        if (path.startsWith('#')) {
            const element = document.getElementById(path.replace('#', ''));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
            return;
        }

        // Builder/editor mode: avoid router navigation; dispatch for builder to handle
        // (editor can be at /builder-v2/:id or /dashboard/store/:id/editor-v2 — both must not navigate to /shop)
        const isBuilder =
            window.location.pathname.startsWith('/builder-v2') ||
            window.location.pathname.includes('/editor-v2');
        if (isBuilder) {
            window.dispatchEvent(new CustomEvent('builder-preview-navigate', { detail: path }));
            return;
        }

        const target = getSmartPath(path);
        // Bare paths like /shop would 404 without a store prefix — prefer inferred base, not marketing home
        if (target === path && path.startsWith('/') && path.length > 1) {
            const inferred = inferStoreBaseFromPathname(window.location.pathname, tenantSlug);
            if (inferred) {
                const suffix = path.startsWith('/') ? path : `/${path}`;
                if (suffix === inferred || suffix.startsWith(`${inferred}/`)) {
                    navigate(suffix);
                } else {
                    navigate(`${inferred}${suffix}`);
                }
                window.scrollTo(0, 0);
                return;
            }
            navigate('/');
            return;
        }
        navigate(target);
        window.scrollTo(0, 0);
    };

    return { smartNavigate, getSmartPath };
}
